import { useEffect, useState } from "react";
import { api, naira, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, PauseCircle, PlayCircle, XCircle, Wallet } from "lucide-react";
import { toast } from "sonner";

export const PROG_STATUS = {
  draft: { label: "Draft", cls: "bg-gray-100 text-gray-600" },
  pending_funding: { label: "Pending Funding", cls: "bg-amber-100 text-amber-800" },
  funding_confirmed: { label: "Funding Confirmed", cls: "bg-blue-100 text-blue-800" },
  active: { label: "Active", cls: "bg-emerald-100 text-emerald-800" },
  suspended: { label: "Suspended", cls: "bg-red-100 text-red-800" },
  completed: { label: "Completed", cls: "bg-gray-200 text-gray-700" },
};

function StatusBadge({ status }) {
  const s = PROG_STATUS[status] || { label: status, cls: "bg-gray-100 text-gray-600" };
  return <Badge data-testid={`prog-status-${status}`} className={s.cls}>{s.label}</Badge>;
}

export function AdminProgrammes({ metrics, onChanged }) {
  const [progs, setProgs] = useState(null);
  const [fund, setFund] = useState(null);
  const [amount, setAmount] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/programmes").then((r) => setProgs(r.data.programmes)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const openFund = (p) => { setAmount(p.total_allocation || p.total_value || 0); setFund(p); };
  const activate = async () => {
    if (Number(amount) <= 0) return toast.error("Enter the confirmed funding amount.");
    setBusy(true);
    try {
      const { data } = await api.post(`/programmes/${fund.id}/activate`, { funding_amount: Number(amount) });
      toast.success(`Funding confirmed — programme activated. ${data.beneficiaries_activated} benefit(s) released.`);
      setFund(null); load(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const setStatus = async (p, status) => {
    try { await api.post(`/programmes/${p.id}/status`, { status }); toast.success(`Programme ${status}`); load(); onChanged && onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const tiles = metrics ? [
    ["Total Beneficiaries", metrics.registered_beneficiaries.toLocaleString()],
    ["Total Allocation", naira(metrics.total_allocation)],
    ["Total Redeemed", naira(metrics.total_redeemed)],
    ["Remaining Balance", naira(metrics.remaining_balance)],
    ["Pending Settlements", naira(metrics.pending_settlements)],
    ["Reversed Transactions", metrics.reversed_transactions],
  ] : [];

  return (
    <div>
      <h2 className="mt-10 font-display font-bold text-xl text-emerald-950">Programme Financials</h2>
      <p className="text-sm text-gray-500">Confirm funding to activate programmes. Total Allocation = active beneficiaries × amount per beneficiary.</p>
      {tiles.length > 0 && (
        <div className="mt-4 grid gap-3 grid-cols-2 lg:grid-cols-6">
          {tiles.map(([l, v]) => (
            <div key={l} className="rounded-2xl border border-emerald-100 bg-white p-4">
              <div className="text-[11px] text-gray-500">{l}</div>
              <div className="mt-1 font-bold text-lg text-emerald-950 tabular-nums">{v}</div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 rounded-2xl border border-emerald-100 bg-white overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-emerald-50/60">
            <TableHead>Programme</TableHead><TableHead>Sponsor</TableHead><TableHead className="text-right">Beneficiaries</TableHead>
            <TableHead className="text-right">Amount / Ben.</TableHead><TableHead className="text-right">Total Allocation</TableHead>
            <TableHead className="text-right">Redeemed</TableHead><TableHead className="text-right">Balance</TableHead>
            <TableHead>Status</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {!progs && <TableRow><TableCell colSpan={9} className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin text-emerald-600 mx-auto" /></TableCell></TableRow>}
            {progs && progs.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-gray-500 py-10">No programmes yet.</TableCell></TableRow>}
            {progs && progs.map((p) => (
              <TableRow key={p.id} data-testid={`admin-prog-${p.code}`}>
                <TableCell><div className="font-medium text-emerald-950">{p.name}</div><div className="font-mono text-[11px] text-emerald-600">{p.code}</div></TableCell>
                <TableCell className="text-sm">{p.sponsor_name}</TableCell>
                <TableCell className="text-right tabular-nums">{(p.active_beneficiaries || 0).toLocaleString()}</TableCell>
                <TableCell className="text-right tabular-nums">{naira(p.amount_per)}</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{naira(p.total_allocation)}</TableCell>
                <TableCell className="text-right tabular-nums text-red-600">{naira(p.redeemed)}</TableCell>
                <TableCell className="text-right tabular-nums">{naira(p.balance)}</TableCell>
                <TableCell><StatusBadge status={p.status} /></TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {["pending_funding", "funding_confirmed", "draft"].includes(p.status) && (
                      <Button data-testid={`activate-${p.code}`} size="sm" onClick={() => openFund(p)} className="bg-emerald-600 hover:bg-emerald-700 h-8"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Confirm Funding & Activate</Button>
                    )}
                    {p.status === "active" && (
                      <Button data-testid={`suspend-${p.code}`} size="sm" variant="outline" onClick={() => setStatus(p, "suspended")} className="h-8 border-red-200 text-red-700"><PauseCircle className="w-3.5 h-3.5 mr-1" /> Suspend</Button>
                    )}
                    {p.status === "suspended" && (
                      <Button data-testid={`reactivate-${p.code}`} size="sm" variant="outline" onClick={() => setStatus(p, "active")} className="h-8 border-emerald-200 text-emerald-700"><PlayCircle className="w-3.5 h-3.5 mr-1" /> Reactivate</Button>
                    )}
                    {["active", "suspended"].includes(p.status) && (
                      <Button data-testid={`close-${p.code}`} size="sm" variant="outline" onClick={() => setStatus(p, "completed")} className="h-8 border-gray-300 text-gray-600"><XCircle className="w-3.5 h-3.5 mr-1" /> Close</Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!fund} onOpenChange={(o) => !o && setFund(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Confirm Funding & Activate</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">Activating <b>{fund?.name}</b> will release all its allocated benefits to beneficiaries and send their SMS notifications. This is recorded in the audit log.</p>
          <div className="mt-2">
            <Label>Confirmed Funding Amount (₦)</Label>
            <Input data-testid="fund-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5 h-11" />
            <p className="mt-1 text-[11px] text-gray-500">Programme value on record: {naira(fund?.total_value || 0)}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFund(null)}>Cancel</Button>
            <Button data-testid="fund-confirm" onClick={activate} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wallet className="w-4 h-4 mr-2" /> Confirm & Activate</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
